<template>

    <div>
        <table
            v-if="product.type === 'complex'"
            class="variations mb-4"
        >
            <tbody>
                <tr
                    v-for="option in product.options"
                >
                    <td>
                        <label>{{ option.name }}</label>
                    </td>
                    <td>
                        <div class="variations-content">
                            <span
                                v-for="value in getValuesForOption(option)"
                                v-tooltip="value.name"
                                :key="'value-' + value.code"
                                :data-term="value.code"
                                :aria-label="value.name"
                                :class="{'optionvalue':true,'selected':valueIsSelected(value),'is_image':value.type === 'image'}"
                                @click="toggleSelectedValue(value)"
                            >
                                <img
                                    v-if="value.type === 'image'"
                                    :src="value.image"
                                    :alt="value.name"
                                />
                                <span
                                    v-else
                                >{{ value.name }}</span>
                            </span>
                        </div>
                    </td>
                </tr>
                <tr
                    v-if="isAuthenticated && selectedVariant !== null"
                >
                    <td>{{ lang('all.prijs') }}</td>
                    <td>
                        <div class="variations-content single">
                            <strong>{{ selectedVariant.price | toCurrency }}</strong>
                        </div>
                    </td>
                </tr>
                <tr
                    v-if="isAuthenticated && selectedVariant !== null"
                >
                    <td>{{ lang('all.voorraad') }}</td>
                    <td>
                        <div class="variations-content single">
                            <span
                                class="stockupdate float-right"
                                v-tooltip="'Toeleveringen bekijken'"
                                style="cursor:pointer;"
                                @click="$emit('ShowRestock', selectedVariant.code)"
                            >
                                {{ lang('all.stockupdate') }}
                                <span>
                                    <i class="mdi mdi-information-variant"></i>
                                </span>
                            </span>
                            {{ selectedVariantStock }}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <div
            class="alert alert-warning"
            v-if="isDiscontinued"
        >
            <h4 class="alert-heading">Let op!</h4>
            <p class="mb-0" v-text="discontinuedText"></p>
        </div>

        <div class="tocart">
            <div>
                <div class="input-group ss-deincrease-function">
                    <div class="input-group-prepend">
                        <button
                            class="btn btn-outline-secondary mb-0"
                            type="button"
                            @click="decreaseQuantity()"
                            :disabled="!hasSelectedAllOptionValues"
                        >-</button>
                    </div>
                    <input
                        type="number"
                        step="1"
                        :title="lang('all.aantal')"
                        size="4"
                        min="0"
                        :max="maxSelectedVariantCount"
                        :disabled="!hasSelectedAllOptionValues"
                        v-model="quantity"
                        @keydown.enter="addToCart"
                    />
                    <div class="input-group-append">
                        <button
                            class="btn btn-outline-secondary mb-0"
                            type="button"
                            @click="increaseQuantity()"
                            :disabled="!hasSelectedAllOptionValues"
                        >+</button>
                    </div>
                </div>
            </div>
            <div>
                <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="!canAddToCart"
                    @click="addToCart"
                >
                    <i class="mdi mdi-cart-outline"></i>
                    {{ lang('all.toevoegen') }}
                </button>
            </div>
        </div>
    </div>

</template>

<script>

import {mapActions} from "vuex";
import axios from "axios";
import Lang from '../../translations';

export default {

    computed: {
        canAddToCart() {
            return this.hasSelectedAllOptionValues &&
                parseInt(this.quantity) > 0 &&
                this.selectedVariant !== null &&
                (
                    !this.isAuthenticated ||
                    parseInt(this.quantity) <= this.selectedVariantStock
                );
        },

        discontinuedText() {
            if (this.hasReplacement) {
                return Lang.get('products.discontinued.with-replacement', {'%replacement%': this.product.replacement});
            }

            if (this.productIsGloballyDiscontinued ||
                this.selectedVariantIsDiscontinued) {
                return Lang.get('products.discontinued.variant');
            }

            return Lang.get('products.discontinued.color');
        },

        hasSelectedAllOptionValues() {
            if (this.product.type === 'simple') {
                return true;
            }

            let has = true;

            this.product.options.forEach(option => {
                if (!this.selectedValues[option.uuid]) {
                    has = false;
                }
            });

            return has;
        },

        hasReplacement() {
            return !!this.product.replacement;
        },

        isAuthenticated() {
            return !!this.$page.props.auth.email;
        },

        isDiscontinued() {
            return this.productIsGloballyDiscontinued ||
                this.selectedVariantIsDiscontinued ||
                this.selectedColorIsDiscontinued;
        },

        productIsGloballyDiscontinued() {
            return this.product.discontinued || false;
        },

        selectedVariantIsDiscontinued() {
            if (!this.selectedVariant) {
                return false;
            }

            let hit = false;

            this.$page.props.discontinued.items.forEach(item => {
                if (this.selectedVariant.code.startsWith(item)) {
                    hit = true;
                }
            });

            return hit;
        },

        selectedColorIsDiscontinued() {
            if (!this.selectedVariant) {
                return false;
            }

            let hit = false;

            let colorCodePart = this.selectedVariant.code.replace(this.product.code, '');

            this.$page.props.discontinued.colorcodes.forEach(item => {
                if (colorCodePart.startsWith(item)) {
                    hit = true;
                }
            });

            return hit;
        },

        maxSelectedVariantCount() {
            if (this.selectedVariant === null) {
                return 0;
            }

            if (!this.isAuthenticated) {
                return 999999;
            }

            return this.selectedVariantHasStock ?
                this.selectedVariantStock :
                0;
        },

        postData() {
            if (this.selectedVariant === null) {
                return {};
            }
            return {
                product: this.selectedVariant.uuid,
                type: this.product.type === 'simple' ? 'product' : 'variant',
                quantity: this.quantity,
            }
        },

        selectedVariant() {
            if (this.product.type === 'simple') {
                return this.product;
            }

            if (!this.hasSelectedAllOptionValues) {
                return null;
            }

            let variant = this.product.variants.filter(variant => this.variantMatchesSelected(variant));

            return variant.length !== 1 ?
                null :
                variant[0];
        },

        selectedVariantHasStock() {
            if (!this.selectedVariant) {
                return false;
            }

            return !!this.stockData[this.selectedVariant.code];
        },

        selectedVariantStock() {
            if (!this.selectedVariant) {
                return 0;
            }

            return this.stockData[this.selectedVariant.code] || 0;
        },
    },

    data() {
        return {
            quantity:0,
            selectedValues:{},
        }
    },

    methods: {
        ...mapActions([
            'fetchCart',
        ]),

        addToCart: _.debounce(function () {
            if (!this.canAddToCart) {
                return;
            }

            this.$GA4.dispatch('add_to_cart');

            axios.post(this.route('api.v1.cart.store'), this.postData)
                .then(() => {
                    this.quantity = 1;
                    this.$snotify.success(this.lang('snotify.success.open.cart.added'));
                    this.fetchCart();
                })
                .catch(e => console.log(e));
        }, 250),

        decreaseQuantity() {
            if (this.quantity < 1) {
                return;
            }

            this.quantity = parseInt(this.quantity) - 1;
        },

        increaseQuantity() {
            if (this.selectedVariant !== null && this.isAuthenticated) {
                if (parseInt(this.quantity) >= this.selectedVariantStock) {
                    return;
                }
            }
            this.quantity = parseInt(this.quantity) + 1;
        },

        getValuesForOption(option) {
            return this.product.option_values.filter(ov => ov.option_uuid === option.uuid);
        },

        toggleSelectedValue(value) {
            this.quantity = 0;
            this.$set(this.selectedValues, value.option_uuid, value.uuid);
            this.$emit('option-selected', value);
            if (this.hasSelectedAllOptionValues) {
                this.$emit('variant-selected', this.selectedVariant);
            }
        },

        valueIsSelected(value) {
            return this.selectedValues[value.option_uuid] &&
                this.selectedValues[value.option_uuid] === value.uuid;
        },

        variantMatchesSelected(variant) {
            let found = {};

            variant.options.forEach(option => {
                if (!this.selectedValues[option.option_uuid]) {
                    found[option.option_uuid] = false;
                } else {
                    found[option.option_uuid] = this.selectedValues[option.option_uuid] === option.uuid;
                }
            });

            return Object.keys(found).length === Object.keys(this.selectedValues).length &&
                Object.values(found).indexOf(false) === -1;
        },
    },

    name:'ProductsAddToCart',

    props: {
        product: {
            required:true,
            type:Object,
        },
        stockData: {
            required:true,
            type:Object,
        },
    },

}

</script>
